import { useMemo, useState } from "react";

export type AdminSortDirection = "asc" | "desc";

export interface AdminSortState<TKey extends string> {
    key: TKey;
    direction: AdminSortDirection;
}

/** In-page sort for plain admin tables (server still owns paging). Pass null to keep the incoming order until a column is clicked. */
export function useAdminTableSort<T, TKey extends string>(
    rows: T[],
    defaultSort: AdminSortState<TKey> | null,
    getSortValue: (row: T, key: TKey) => string | number | null | undefined
) {
    const [sort, setSort] = useState<AdminSortState<TKey> | null>(defaultSort);

    const toggleSort = (key: TKey) => {
        setSort((prev) =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    const sortedRows = useMemo(() => {
        if (!sort) return rows;
        const next = [...rows];
        next.sort((a, b) => {
            const av = getSortValue(a, sort.key);
            const bv = getSortValue(b, sort.key);
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp =
                typeof av === "number" && typeof bv === "number"
                    ? av - bv
                    : String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
            return sort.direction === "asc" ? cmp : -cmp;
        });
        return next;
    }, [rows, sort, getSortValue]);

    return { sort, toggleSort, sortedRows };
}
